<template>
  <div class="min-h-screen flex flex-col bg-lightGray">
    <GradientBlobs />
    <nav
      class="fixed top-0 left-0 right-0 z-40 flex items-center h-12 min-h-[3rem] bg-white border-b border-gray-200 px-4"
    >
      <router-link :to="logoTo" class="flex items-center shrink-0">
        <img src="/campus_logo.svg" alt="Campus" class="h-7" />
      </router-link>

      <div class="flex items-center gap-2 ml-auto">
        <ProfileDropdown />
      </div>
    </nav>

    <CampusSidebar />

    <div
      class="relative z-10 pt-12 flex-grow flex flex-col transition-[padding-left] duration-300 ease-in-out"
      :class="isSidebarCollapsed ? 'md:pl-16' : 'md:pl-60'"
    >
      <GraduationBanner />
      <GuideCampusFellowBar v-if="showGuideFellowBar" />
      <div class="flex-grow p-4">
        <router-view v-slot="{ Component, route: currentRoute }">
          <transition name="page-fade" mode="out-in" appear>
            <component :is="Component" :key="currentRoute.path" />
          </transition>
        </router-view>
      </div>
      <!-- <DashboardFooter /> -->
    </div>

    <button
      class="md:hidden fixed bottom-4 left-4 z-50 flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-full shadow-md"
      @click="isMobileSidebarOpen = !isMobileSidebarOpen"
      aria-label="Toggle Sidebar"
    >
      <Menu class="w-5 h-5 text-gray-600" />
    </button>

    <div
      v-if="isMobileSidebarOpen"
      class="md:hidden fixed inset-0 bg-black/30 z-30"
      @click="isMobileSidebarOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, provide, onMounted, onUnmounted, computed, watch } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { useRoute } from 'vue-router'
import { Menu } from 'lucide-vue-next'
import { useLocalStorage } from '@vueuse/core'
import ProfileDropdown from '@/components/common/ProfileDropdown.vue'
import GraduationBanner from '@/components/common/GraduationBanner.vue'
import CampusSidebar from '@/components/layout/CampusSidebar.vue'
import GuideCampusFellowBar from '@/components/campus/GuideCampusFellowBar.vue'
import { useUserStore } from '@/stores/user'
import { useCampusStore } from '@/stores/campus'
import { useAuth } from '@/composables/useAuth'
import { useGuideMode } from '@/composables/useGuideMode'
import { useAvatarCheck } from '@/composables/useAvatarCheck'
import GradientBlobs from '@/components/ui/GradientBlobs.vue'

const route = useRoute()
const isMobileSidebarOpen = ref(false)
const isSidebarCollapsed = useLocalStorage('sidebar-collapsed', false)
provide('isMobileSidebarOpen', isMobileSidebarOpen)
provide('isSidebarCollapsed', isSidebarCollapsed)

const userStore = useUserStore()
const campusStore = useCampusStore()
const { isGuide } = useAuth()
const { isGuideMode, campusLink } = useGuideMode()
const { checkAvatarOrRedirect } = useAvatarCheck()

const logoTo = computed<RouteLocationRaw>(() =>
  isGuideMode.value ? campusLink('/curriculum/plan') : '/home',
)

const showGuideFellowBar = computed(
  () => !!(isGuide.value && route.query.userId),
)

watch(
  () => ({
    userId: route.query.userId as string | undefined,
    podId: route.query.podId as string | undefined,
    fellowName: route.query.fellowName as string | undefined,
    isG: isGuide.value,
  }),
  async ({ userId, podId, fellowName, isG }) => {
    if (userId && isG) {
      campusStore.setGuideTarget(userId)
      await campusStore.fetchGuideFellowDisplay(userId, podId, fellowName)
    } else {
      campusStore.setGuideTarget(null)
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  const hadGuideTarget = !!campusStore.targetUserId
  campusStore.setGuideTarget(null)
  if (hadGuideTarget) {
    campusStore.$patch({
      personalCurriculum: null,
      planSteps: [],
      hasPersonalCurriculum: null,
    })
  }
})

onMounted(async () => {
  await userStore.fetchCurrentUser()
  checkAvatarOrRedirect()
})
</script>
